/**
 * Forge usage panel (ADR-023 D8): this month's active hours against the
 * tier's included hours and hard cap, snapshot storage, credits settled this
 * month, and the two holds. Read-only; the numbers are the same the start /
 * turn-open gates read, so what the user sees here is what the gate will say.
 */
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Clock, Coins, HardDrive } from 'lucide-react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { ProgressBar } from '@/components/ui/progress';
import { forgeQueryKeys } from '../api/queryKeys';
import type { ForgeUsage } from '../types';

const USAGE_STALE_MS = 30_000;

export function formatForgeHours(seconds: number): string {
  const hours = seconds / 3600;
  if (hours === 0) return '0 h';
  if (hours < 0.1) return `${Math.max(1, Math.round(seconds / 60))} min`;
  // One decimal below 10 h unless it is a whole number of hours ("2 h", "4.5 h", "40 h").
  const decimals = hours < 10 && Math.round(hours * 10) % 10 !== 0 ? 1 : 0;
  return `${hours.toFixed(decimals)} h`;
}

export function formatForgeStorage(bytes: number): string {
  if (bytes <= 0) return '0 MB';
  const mb = bytes / 1_000_000;
  if (mb < 1000) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  return `${(mb / 1000).toFixed(2)} GB`;
}

export function formatForgeCredits(credits: number): string {
  return credits.toFixed(credits > 0 && credits < 0.01 ? 4 : 2);
}

export interface ForgeUsagePanelProps {
  /** Injected for tests; the page uses the live `api`. */
  fetchUsage?: () => Promise<ForgeUsage>;
}

export function ForgeUsagePanel({ fetchUsage }: ForgeUsagePanelProps) {
  const query = useQuery({
    queryKey: forgeQueryKeys.usage(),
    queryFn: fetchUsage ?? (() => api.getForgeUsage()),
    staleTime: USAGE_STALE_MS,
  });

  if (query.isLoading) {
    return (
      <Card className="mb-6" data-testid="forge-usage-loading">
        <CardContent className="py-4 text-sm text-muted-foreground">Loading usage…</CardContent>
      </Card>
    );
  }
  if (query.error || !query.data) {
    return (
      <Card className="mb-6" data-testid="forge-usage-error">
        <CardContent className="py-4 text-sm text-muted-foreground">
          Usage is unavailable right now.
        </CardContent>
      </Card>
    );
  }

  const usage = query.data;
  const { activeSeconds, hardCapSeconds, chargedCredits } = usage.month;
  const capPercent = hardCapSeconds > 0 ? (activeSeconds / hardCapSeconds) * 100 : 0;
  const atCap = hardCapSeconds > 0 && activeSeconds >= hardCapSeconds;

  return (
    <div className="mb-6 space-y-3" data-testid="forge-usage-panel">
      {usage.userHold && (
        <Card className="border-destructive" role="alert">
          <CardContent className="py-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">Fias Forge is unavailable for this account</p>
              <p className="text-sm text-muted-foreground">{usage.userHold.reason}</p>
            </div>
          </CardContent>
        </Card>
      )}
      {usage.billingHold && (
        <Card className="border-destructive" role="alert">
          <CardContent className="py-4 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
            <div>
              <p className="font-medium">A Forge charge could not be collected</p>
              <p className="text-sm text-muted-foreground">
                Add credits to lift the hold — the charge is retried daily and your forges cannot
                start or take turns until it succeeds.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardContent className="py-4 grid gap-4 sm:grid-cols-3">
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Clock className="h-4 w-4" />
              Active hours this month
            </div>
            <p className="text-lg font-semibold mt-1" data-testid="forge-usage-hours">
              {formatForgeHours(activeSeconds)}
            </p>
            <p className="text-xs text-muted-foreground" data-testid="forge-usage-rate">
              {usage.computeCreditsPerHour.standard} credits per hour while a forge runs (
              {usage.computeCreditsPerHour.large} on Large)
            </p>
            {hardCapSeconds > 0 ? (
              <>
                <ProgressBar
                  value={capPercent}
                  className="h-2 mt-2 [&>span]:hidden"
                  aria-label="Share of the monthly hard cap used"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  {atCap
                    ? `Monthly cap of ${formatForgeHours(hardCapSeconds)} reached`
                    : `Monthly cap ${formatForgeHours(hardCapSeconds)}`}
                </p>
              </>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                Forge is available on the Standard and Pro plans.
              </p>
            )}
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <HardDrive className="h-4 w-4" />
              Workspace storage
            </div>
            <p className="text-lg font-semibold mt-1" data-testid="forge-usage-storage">
              {formatForgeStorage(usage.storageBytes)}
            </p>
            <p className="text-xs text-muted-foreground mt-1">Snapshots across all your forges</p>
          </div>
          <div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Coins className="h-4 w-4" />
              Charged this month
            </div>
            <p className="text-lg font-semibold mt-1" data-testid="forge-usage-credits">
              {formatForgeCredits(chargedCredits)} credits
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Running time plus storage, settled daily. Claude's model use is charged per turn.
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
